'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

export type LivoraRole = 'super_admin' | 'admin' | 'manager' | 'executive' | 'viewer';
export type BrandVertical = 'profinder' | 'petify' | 'livora-fc' | 'tumble-gym' | 'all';

export interface LivoraProfile {
  id: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  phone: string | null;
  role: LivoraRole;
  brand: BrandVertical;
  department: string | null;
  city: string | null;
  managerId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: LivoraProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasRole: (roles: LivoraRole[]) => boolean;
  isAdmin: () => boolean;
  isManagerOrAbove: () => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function mapProfile(data: Record<string, unknown>): LivoraProfile {
  return {
    id: data.id as string,
    email: data.email as string,
    fullName: (data.full_name as string) || '',
    avatarUrl: data.avatar_url as string | null,
    phone: data.phone as string | null,
    role: (data.role as LivoraRole) || 'executive',
    brand: (data.brand as BrandVertical) || 'all',
    department: data.department as string | null,
    city: data.city as string | null,
    managerId: data.manager_id as string | null,
    isActive: (data.is_active as boolean) ?? true,
    createdAt: data.created_at as string,
    updatedAt: data.updated_at as string,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<LivoraProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const router = useRouter();

  const fetchProfile = useCallback(
    async (userId: string): Promise<LivoraProfile | null> => {
      try {
        const { data, error } = await supabase
          .from('livora_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error || !data) return null;
        return mapProfile(data as Record<string, unknown>);
      } catch {
        return null;
      }
    },
    [supabase]
  );

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, supabase.auth]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Email or password is incorrect.');
      }
      if (error.message.includes('Email not confirmed')) {
        throw new Error('Your email address has not been verified. Please check your inbox.');
      }
      if (error.message.includes('User not found')) {
        throw new Error('No account found with this email address.');
      }
      throw new Error(error.message);
    }
    router.refresh();
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
    setProfile(null);
    router.push('/sign-in-login-screen');
    router.refresh();
  };

  const signUp = async (
    email: string,
    password: string,
    metadata: Record<string, string> = {}
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw new Error(error.message);
  };

  const sendPasswordResetEmail = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/reset-password`,
    });
    if (error) throw new Error(error.message);
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(error.message);
  };

  const refreshProfile = async () => {
    if (!user) return;
    const p = await fetchProfile(user.id);
    setProfile(p);
  };

  const hasRole = (roles: LivoraRole[]): boolean => {
    if (!profile) return false;
    return roles.includes(profile.role);
  };

  const isAdmin = (): boolean => hasRole(['super_admin', 'admin']);
  const isManagerOrAbove = (): boolean => hasRole(['super_admin', 'admin', 'manager']);

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signIn,
    signOut,
    signUp,
    sendPasswordResetEmail,
    updatePassword,
    refreshProfile,
    hasRole,
    isAdmin,
    isManagerOrAbove,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
